import { useSetAtom } from 'jotai'
import { InputText, RadioButton } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import { useIsIkyuPointEnabled } from '~/features/reservationInput/store/stateMachine/context'
import { money } from '~/lib/money/money'
import { css, sva } from '~/styled-system/css'
import {
  changeIkyuPointUsage$,
  useIsIkyuPointVisible,
  useTotalIkyuPoint,
} from '../../store/payment'
import { useIkyuPointUsageError } from '../../store/userInput'
import { usePointsUsage } from './usePointsUsage'

export const IkyuOnlyIkyuPointsUsage =
  import.meta.env.VITE_MODE === 'ppg' ? EmptyIkyuPointsUsage : IkyuPointsUsage

function EmptyIkyuPointsUsage() {
  return null
}

const styles = sva({
  slots: ['container', 'head', 'title', 'content'],
  base: {
    container: {
      bg: 'white',
      paddingX: '3',
      paddingY: '4',
      spaceY: '3',
      _pc: {
        padding: '6',
      },
    },
    head: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontWeight: 'bold',
    },
    title: {
      fontSize: 'xl',
      fontWeight: 'bold',
      _pc: {
        fontSize: '2xl',
      },
    },
    content: {
      spaceY: '4',
      _pc: {
        w: '[424px]',
        ml: 'auto',
        border: 'medium',
        borderRadius: 'default',
        p: '4',
      },
    },
  },
})

function IkyuPointsUsage() {
  const isVisible = useIsIkyuPointVisible()
  const classes = styles()
  if (!isVisible) {
    return null
  }
  return (
    <div className={classes.container}>
      <Contents />
    </div>
  )
}

function useContents() {
  const totalPoint = useTotalIkyuPoint()
  const enabled = useIsIkyuPointEnabled()
  const change = useSetAtom(changeIkyuPointUsage$)
  const onChange = useCallback(
    (v: boolean) => {
      return () => change(v)
    },
    [change]
  )
  return { totalPoint, enabled, onChange }
}

function Contents() {
  const { totalPoint, enabled, onChange } = useContents()
  const classes = styles()
  if (totalPoint === 0) {
    return (
      <>
        <div className={classes.head}>
          <h2 className={classes.title}>保有ポイント</h2>
          <p className="text-lg">保有ポイントはありません</p>
        </div>
      </>
    )
  }
  return (
    <>
      <div className={classes.head}>
        <h2 className={classes.title}>保有ポイント</h2>
        <span className={css({ fontSize: 'lg' })}>{money(totalPoint)}ポイント</span>
      </div>
      <div className={classes.content}>
        <RadioButton name="ikyuPointsEnabled" checked={!enabled} onChange={onChange(false)}>
          利用しない
        </RadioButton>
        <div className="space-y-2">
          <RadioButton name="ikyuPointsEnabled" checked={enabled} onChange={onChange(true)}>
            利用する
          </RadioButton>
          <Input />
        </div>
      </div>
    </>
  )
}

function Input() {
  const enabled = useIsIkyuPointEnabled()
  if (!enabled) {
    return null
  }
  return (
    <div className="ml-7 space-y-2 rounded bg-neutral-100 p-3">
      <PointsUsage />
      <p className="text-sm text-neutral-700">※10ポイント以上、1ポイント単位で利用できます</p>
      <ErrorMessage />
    </div>
  )
}

function PointsUsage() {
  const { onChangeAllIkyuPointUsage, isAllIkyuPointApplied, availableMaxIkyuPoint } =
    usePointsUsage()
  return (
    <div className="space-y-4">
      <div>
        <RadioButton
          name="pointUsageType"
          value="ALL"
          checked={isAllIkyuPointApplied}
          onChange={onChangeAllIkyuPointUsage(true)}
          labelClassName="items-center"
        >
          <p className="ml-1">
            すべて利用する
            <span className={css({ fontWeight: 'bold', fontSize: 'sm', ml: '2' })}>
              {money(availableMaxIkyuPoint)}
              <span className={css({ fontSize: 'xs' })}>ポイント</span>
            </span>
          </p>
        </RadioButton>
      </div>
      <div className="space-y-2">
        <RadioButton
          name="pointUsageType"
          value="PARTIAL"
          checked={!isAllIkyuPointApplied}
          onChange={onChangeAllIkyuPointUsage(false)}
          labelClassName="items-center"
        >
          <p className="ml-1">一部利用する</p>
        </RadioButton>
        <div className="ml-7">
          <InputIkyuPointUsage />
        </div>
      </div>
    </div>
  )
}

function InputIkyuPointUsage() {
  const { ikyuPointAmount, isAllIkyuPointApplied, onChangeIkyuPointAmount } = usePointsUsage()
  if (isAllIkyuPointApplied) {
    return null
  }
  return (
    <>
      <div className="space-x-2">
        <InputText
          width={110}
          inputMode="numeric"
          value={ikyuPointAmount || ''}
          onChange={onChangeIkyuPointAmount}
        />
        <span>ポイント</span>
      </div>
    </>
  )
}

function ErrorMessage() {
  const error = useIkyuPointUsageError()
  if (error === undefined) {
    return null
  }
  return <p className="text-sm text-accent-pink">{error}</p>
}
